import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ApiError, getGenerationPrompt } from '@platform/client';
import { useQuery } from '@tanstack/react-query';

import { describeApiError } from '../api-error';

import { PromptBlock } from './PromptBlock';

import type { GenerationDetail } from '@platform/schema/generation';

/** What the job recorded for an agent that ran with no skills. */
const NO_SKILLS = 'none';

const notRecorded = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 404;

/**
 * Cached for good, and apart from the job's own query: that one polls every two seconds
 * while the job runs, and the text behind a digest cannot change.
 */
const promptQuery = (digest: string) => ({
  queryKey: ['generation-prompt', digest],
  queryFn: async () =>
    (await getGenerationPrompt({ path: { digest }, throwOnError: true })).data,
  staleTime: Infinity,
  gcTime: Infinity,
  retry: (failures: number, error: unknown) =>
    !notRecorded(error) && failures < 1,
});

interface Agent {
  readonly name: string;
  readonly skills: readonly string[];
  readonly digest: string;
}

/** Each agent's system prompt, by the digest the job recorded — ADMIN-25. */
export function JobPrompts({
  job,
}: {
  job: Pick<
    GenerationDetail,
    | 'skillSet'
    | 'skillDigest'
    | 'reviewerSkillSet'
    | 'reviewerSkillDigest'
    | 'editorSkillSet'
    | 'editorSkillDigest'
  >;
}) {
  const agents: readonly Agent[] = [
    { name: 'Planner', skills: job.skillSet, digest: job.skillDigest },
    {
      name: 'Reviewer',
      skills: job.reviewerSkillSet,
      digest: job.reviewerSkillDigest,
    },
    {
      name: 'Editor',
      skills: job.editorSkillSet,
      digest: job.editorSkillDigest,
    },
  ];

  return (
    <Accordion
      variant="outlined"
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary aria-controls="job-prompts" id="job-prompts-summary">
        <Typography variant="subtitle1">Prompts</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {agents.map((agent) => (
          <AgentPrompt key={agent.name} agent={agent} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

function AgentPrompt({ agent }: { agent: Agent }) {
  const id = `job-prompt-${agent.name.toLowerCase()}`;
  const ran = agent.digest !== NO_SKILLS;

  return (
    <Accordion
      variant="outlined"
      disableGutters
      disabled={!ran}
      // Unmounted while closed, so the text is fetched only once someone opens the row.
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary aria-controls={`${id}-text`} id={id}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2">{agent.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {ran
              ? `${agent.skills.join(', ')} · ${agent.digest}`
              : 'No skills recorded'}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {ran && <PromptText digest={agent.digest} />}
      </AccordionDetails>
    </Accordion>
  );
}

function PromptText({ digest }: { digest: string }) {
  const prompt = useQuery(promptQuery(digest));

  if (prompt.isPending) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading…
      </Typography>
    );
  }

  if (prompt.isError) {
    return notRecorded(prompt.error) ? (
      <Typography variant="body2" color="text.secondary">
        Text not recorded for jobs before GEN-192
      </Typography>
    ) : (
      <Alert severity="error">{describeApiError(prompt.error)}</Alert>
    );
  }

  return <PromptBlock text={prompt.data.text} />;
}
